/**
 * useSubgeoColors Hook
 *
 * A high-performance React hook for managing subgeo-based neighborhood colors.
 * Provides memoized color calculations and efficient cache invalidation.
 *
 * Features:
 * - Automatic color generation when subgeos change
 * - Memoized calculations to prevent unnecessary re-renders
 * - Efficient neighborhood-to-color mapping
 * - Support for dynamic neighborhood reassignment
 */

import { useMemo } from 'react'
import { useSubgeoStore } from '../store/subgeo-store'
import type { Subgeo } from '../types'
import {
	type ColorPair,
	createNeighborhoodColorMap,
	DEFAULT_NEIGHBORHOOD_COLOR,
	DETACHED_NEIGHBORHOOD_COLOR,
	generateSubgeoHash,
	getNeighborhoodColor
} from '../utils/color-utils'

/**
 * Return type for useSubgeoColors hook
 */
export interface UseSubgeoColorsReturn {
	/** Map of subgeo IDs to their assigned colors */
	subgeoColors: Map<string, ColorPair>

	/** Map of neighborhood IDs to their fill colors */
	neighborhoodColorMap: Map<string, string>

	/** Function to get color for a specific neighborhood */
	getColorForNeighborhood: (
		neighborhoodId: string,
		isSelected: boolean
	) => ColorPair

	/** Hash of current subgeo configuration for cache invalidation */
	subgeoHash: string

	/** Whether colors are currently being calculated */
	isCalculating: boolean
}

/**
 * Custom hook for managing subgeo-based neighborhood colors
 *
 * This hook provides a complete color management system for neighborhoods
 * based on their subgeo assignments. It handles:
 *
 * - Deterministic color generation for subgeos
 * - Efficient neighborhood-to-color mapping
 * - Memoization to prevent unnecessary recalculations
 * - Support for selection state override
 *
 * Performance optimizations:
 * - Colors are only recalculated when subgeo assignments change
 * - Memoized functions prevent unnecessary re-renders
 * - Hash-based cache invalidation for precise updates
 *
 * @param subgeos - Array of subgeo objects with neighborhood assignments
 * @returns Object containing color maps and utility functions
 *
 * @example
 * ```typescript
 * const MyComponent = () => {
 *   const {subgeos} = useSubgeoStore()
 *   const {
 *     subgeoColors,
 *     neighborhoodColorMap,
 *     getColorForNeighborhood
 *   } = useSubgeoColors(subgeos)
 *
 *   const handleNeighborhoodStyle = (neighborhoodId: string, isSelected: boolean) => {
 *     const colors = getColorForNeighborhood(neighborhoodId, isSelected)
 *     return {
 *       fillColor: colors.fill,
 *       color: colors.border,
 *       fillOpacity: colors.fillOpacity,
 *       weight: isSelected ? 2 : 1
 *     }
 *   }
 *
 *   return <div>...</div>
 * }
 * ```
 */
export function useSubgeoColors(
	subgeos: Subgeo[],
	editingSubgeoId?: string | null
): UseSubgeoColorsReturn {
	const subgeoColorMap = useSubgeoStore((state) => state.subgeoColorMap)
	const detachedNeighborhoods = useSubgeoStore(
		(state) => state.detachedNeighborhoods
	)
	const highlightedNeighborhoodId = useSubgeoStore(
		(state) => state.highlightedNeighborhoodId
	)

	/**
	 * Generate hash of subgeo configuration for memoization
	 * This hash changes only when subgeo assignments change
	 */
	const subgeoHash = useMemo(() => {
		return generateSubgeoHash(subgeos)
	}, [subgeos])

	/**
	 * Get color palette from Zustand store
	 * Colors are persisted and only change on page reload or geo change
	 */
	const subgeoColors = useMemo(() => {
		if (subgeos.length === 0) {
			return new Map<string, ColorPair>()
		}

		return subgeoColorMap
	}, [subgeos.length, subgeoColorMap])

	/**
	 * Create neighborhood-to-color mapping
	 * Provides O(1) lookup time for neighborhood colors
	 */
	const neighborhoodColorMap = useMemo(() => {
		if (subgeos.length === 0 || subgeoColors.size === 0) {
			return new Map<string, string>()
		}

		return createNeighborhoodColorMap(subgeos, subgeoColors)
	}, [subgeos, subgeoColors])

	/**
	 * Memoized function to get color for a specific neighborhood
	 * Handles highlight state, selection state priority, edit mode, detached state, and fallback to default colors
	 */
	const getColorForNeighborhood = useMemo(() => {
		const getHighlightedColor = (neighborhoodId: string): ColorPair => {
			if (neighborhoodId === highlightedNeighborhoodId) {
				return getNeighborhoodColor(
					neighborhoodId,
					true,
					neighborhoodColorMap
				)
			}
			return { ...DEFAULT_NEIGHBORHOOD_COLOR, fillOpacity: 0.3 }
		}

		const getEditModeColor = (
			neighborhoodId: string,
			isSelected: boolean
		): ColorPair | null => {
			if (!editingSubgeoId) {
				return null
			}
			const editedSubgeo = subgeos.find((s) => s.id === editingSubgeoId)
			if (!editedSubgeo) {
				return null
			}

			if (detachedNeighborhoods.has(neighborhoodId)) {
				return DETACHED_NEIGHBORHOOD_COLOR
			}

			const isInEditedSubgeo =
				editedSubgeo.neighborhoodIds.includes(neighborhoodId)

			if (isInEditedSubgeo || isSelected) {
				return getNeighborhoodColor(
					neighborhoodId,
					isSelected,
					neighborhoodColorMap
				)
			}

			return DEFAULT_NEIGHBORHOOD_COLOR
		}

		return (neighborhoodId: string, isSelected: boolean): ColorPair => {
			if (highlightedNeighborhoodId) {
				return getHighlightedColor(neighborhoodId)
			}

			const editColor = getEditModeColor(neighborhoodId, isSelected)
			if (editColor) {
				return editColor
			}

			return getNeighborhoodColor(
				neighborhoodId,
				isSelected,
				neighborhoodColorMap
			)
		}
	}, [
		neighborhoodColorMap,
		editingSubgeoId,
		subgeos,
		detachedNeighborhoods,
		highlightedNeighborhoodId
	])

	/**
	 * Calculate if colors are currently being processed
	 * Useful for showing loading states during color generation
	 */
	const isCalculating = useMemo(() => {
		return (
			subgeos.length > 0 &&
			(subgeoColors.size === 0 || neighborhoodColorMap.size === 0)
		)
	}, [subgeos, subgeoColors.size, neighborhoodColorMap.size])

	return {
		subgeoColors,
		neighborhoodColorMap,
		getColorForNeighborhood,
		subgeoHash,
		isCalculating
	}
}

/**
 * Lightweight hook for getting just the color function
 * Useful when you only need to style neighborhoods without accessing the full color maps
 *
 * @param subgeos - Array of subgeo objects
 * @returns Function to get color for a neighborhood
 *
 * @example
 * ```typescript
 * const MyMapComponent = () => {
 *   const {subgeos} = useSubgeoStore()
 *   const getNeighborhoodColor = useNeighborhoodColorFunction(subgeos)
 *
 *   const styleNeighborhood = (id: string, isSelected: boolean) => {
 *     const colors = getNeighborhoodColor(id, isSelected)
 *     return {
 *       fillColor: colors.fill,
 *       color: colors.border
 *     }
 *   }
 *
 *   return <LeafletMap styleFunction={styleNeighborhood} />
 * }
 * ```
 */
export function useNeighborhoodColorFunction(
	subgeos: Subgeo[],
	editingSubgeoId?: string | null
): (neighborhoodId: string, isSelected: boolean) => ColorPair {
	const { getColorForNeighborhood } = useSubgeoColors(
		subgeos,
		editingSubgeoId
	)
	return getColorForNeighborhood
}

/**
 * Hook for accessing subgeo color legend data
 * Provides formatted data for displaying color legends in the UI
 *
 * @param subgeos - Array of subgeo objects
 * @returns Array of legend items with subgeo names and colors
 *
 * @example
 * ```typescript
 * const ColorLegend = () => {
 *   const {subgeos} = useSubgeoStore()
 *   const legendItems = useSubgeoColorLegend(subgeos)
 *
 *   return (
 *     <div>
 *       {legendItems.map(item => (
 *         <div key={item.id} style={{color: item.color}}>
 *           {item.name}
 *         </div>
 *       ))}
 *     </div>
 *   )
 * }
 * ```
 */
export interface SubgeoLegendItem {
	id: string
	name: string
	color: string
	neighborhoodCount: number
}

export function useSubgeoColorLegend(subgeos: Subgeo[]): SubgeoLegendItem[] {
	const { subgeoColors } = useSubgeoColors(subgeos)

	return useMemo(() => {
		return subgeos
			.map((subgeo) => {
				const colorPair = subgeoColors.get(subgeo.id)
				return {
					id: subgeo.id,
					name: subgeo.name,
					color: colorPair?.fill || '#3b82f6',
					neighborhoodCount: subgeo.neighborhoodIds.length
				}
			})
			.sort((a, b) => a.name.localeCompare(b.name))
	}, [subgeos, subgeoColors])
}
